import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Loader2,
  Users,
  CreditCard,
  FileSignature,
  Save,
  LayoutGrid,
  DollarSign,
  Receipt,
  Camera,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

interface VistoriadoraModulos {
  id: string;
  nome: string;
  artMultipla: boolean;
  laudosGratisPorCliente: number;
  creditosPrePagos: boolean;
  splitPagamento: boolean;
  nfeAtivado: boolean;
  detranVeiculoFoto: boolean;
  chavePix?: string | null;
  valorPorLaudo?: number | null;
  art?: string | null;
  vencimentoArt?: string | null;
}

interface ModuloConfig {
  key: keyof Omit<VistoriadoraModulos, "id" | "nome">;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  hasQuantity?: boolean;
  quantityLabel?: string;
}

const MODULOS: ModuloConfig[] = [
  {
    key: "laudosGratisPorCliente",
    label: "Laudos por Associados/Mês",
    description: "Limite de laudos que o parceiro pode criar por conta própria/mês. Acima do limite, Admin/RT deve autorizar.",
    icon: Users,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    hasQuantity: true,
    quantityLabel: "laudos/mês",
  },
  {
    key: "artMultipla",
    label: "ART Múltipla",
    description: "Vistoriadora arca com a ART de todos os parceiros",
    icon: FileSignature,
    color: "text-violet-600",
    bgColor: "bg-violet-50",
    borderColor: "border-violet-200",
  },
  {
    key: "creditosPrePagos",
    label: "Créditos Pré-Pagos",
    description: "Parceiros compram créditos antecipados para emitir laudos",
    icon: CreditCard,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  {
    key: "nfeAtivado",
    label: "Nota Fiscal (Bling)",
    description: "Permite emitir NFS-e (via Bling) das compras de crédito. Exige Créditos Pré-Pagos também ativo.",
    icon: Receipt,
    color: "text-teal-600",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
  },
  {
    key: "detranVeiculoFoto",
    label: "Detran Veículo/Foto",
    description: "Parceiros enviam frente, traseira e laterais do veículo; o técnico só é atribuído com as 4 fotos. Vale para laudos criados depois de ligar.",
    icon: Camera,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
  },
];

// Busca pela chave: o cartão não pode depender da posição do módulo na lista.
const moduloPorChave = (chave: ModuloConfig["key"]) => MODULOS.find((m) => m.key === chave)!;

function ModuloCard({
  modulo,
  ativo,
  quantidade,
  onToggle,
  onQuantidade,
}: {
  modulo: ModuloConfig;
  ativo: boolean;
  quantidade?: number;
  onToggle: (val: boolean) => void;
  onQuantidade?: (val: number) => void;
}) {
  const Icon = modulo.icon;
  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-all duration-200",
        ativo
          ? cn(modulo.bgColor, modulo.borderColor)
          : "bg-slate-50 border-slate-200 opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className={cn(
              "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
              ativo ? "bg-white shadow-sm" : "bg-slate-100",
            )}
          >
            <Icon className={cn("w-4 h-4", ativo ? modulo.color : "text-slate-400")} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={cn("text-sm font-semibold", ativo ? "text-slate-900" : "text-slate-500")}>
              {modulo.label}
            </p>
            <p className="text-xs text-slate-500 mt-0.5 leading-snug">{modulo.description}</p>
            {modulo.hasQuantity && ativo && onQuantidade && (
              <div className="flex items-center gap-2 mt-2.5">
                <Input
                  type="number"
                  min={1}
                  value={quantidade ?? 0}
                  onChange={(e) => onQuantidade(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 h-7 text-center text-sm font-semibold px-2"
                />
                <span className="text-xs text-slate-500">{modulo.quantityLabel}</span>
              </div>
            )}
          </div>
        </div>
        <Switch
          checked={ativo}
          onCheckedChange={onToggle}
          className="shrink-0 mt-0.5"
        />
      </div>
    </div>
  );
}

function VistoriadoraCard({ v, token }: { v: VistoriadoraModulos; token: string }) {
  const { toast } = useToast();

  const [artMultipla, setArtMultipla] = useState(v.artMultipla);
  const [creditosPrePagos, setCreditosPrePagos] = useState(v.creditosPrePagos);
  const [nfeAtivado, setNfeAtivado] = useState(v.nfeAtivado);
  const [detranVeiculoFoto, setDetranVeiculoFoto] = useState(v.detranVeiculoFoto ?? false);
  const [laudosAtivo, setLaudosAtivo] = useState(v.laudosGratisPorCliente > 0);
  const [laudosQtd, setLaudosQtd] = useState(
    v.laudosGratisPorCliente > 0 ? v.laudosGratisPorCliente : 1,
  );
  const [valorPorLaudo, setValorPorLaudo] = useState<string>(
    v.valorPorLaudo != null ? String(v.valorPorLaudo) : "",
  );
  const [art, setArt] = useState(v.art ?? "");
  const [vencimentoArt, setVencimentoArt] = useState(v.vencimentoArt ?? "");

  const laudosGratisPorCliente = laudosAtivo ? laudosQtd : 0;

  const dirty =
    artMultipla !== v.artMultipla ||
    creditosPrePagos !== v.creditosPrePagos ||
    nfeAtivado !== v.nfeAtivado ||
    detranVeiculoFoto !== (v.detranVeiculoFoto ?? false) ||
    laudosGratisPorCliente !== v.laudosGratisPorCliente ||
    valorPorLaudo !== (v.valorPorLaudo != null ? String(v.valorPorLaudo) : "") ||
    art !== (v.art ?? "") ||
    vencimentoArt !== (v.vencimentoArt ?? "");

  const activeCount = [artMultipla, creditosPrePagos, laudosAtivo, nfeAtivado, detranVeiculoFoto].filter(Boolean).length;

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/owner/modulos/${v.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          artMultipla,
          laudosGratisPorCliente,
          creditosPrePagos,
          splitPagamento: true,
          nfeAtivado,
          detranVeiculoFoto,
          valorPorLaudo: valorPorLaudo ? Number(valorPorLaudo) : null,
          art: art.trim() || undefined,
          vencimentoArt: vencimentoArt || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao salvar");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/modulos"] });
      toast({ title: `${v.nome} — módulos salvos` });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4 text-slate-500" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold text-slate-900">{v.nome}</CardTitle>
              <p className="text-xs text-slate-400 mt-0.5">
                {activeCount === 0
                  ? "Nenhum módulo ativo"
                  : `${activeCount} módulo${activeCount > 1 ? "s" : ""} ativo${activeCount > 1 ? "s" : ""}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                Alterado
              </Badge>
            )}
            <Button
              size="sm"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !dirty}
              variant={dirty ? "default" : "ghost"}
            >
              {mutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5 mr-1.5" />
              )}
              Salvar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ModuloCard
            modulo={moduloPorChave("laudosGratisPorCliente")}
            ativo={laudosAtivo}
            quantidade={laudosQtd}
            onToggle={(val) => {
              setLaudosAtivo(val);
              if (val && laudosQtd === 0) setLaudosQtd(1);
            }}
            onQuantidade={setLaudosQtd}
          />
          <ModuloCard
            modulo={moduloPorChave("artMultipla")}
            ativo={artMultipla}
            onToggle={setArtMultipla}
          />
          <ModuloCard
            modulo={moduloPorChave("creditosPrePagos")}
            ativo={creditosPrePagos}
            onToggle={setCreditosPrePagos}
          />
          <ModuloCard
            modulo={moduloPorChave("nfeAtivado")}
            ativo={nfeAtivado}
            onToggle={setNfeAtivado}
          />
          <ModuloCard
            modulo={moduloPorChave("detranVeiculoFoto")}
            ativo={detranVeiculoFoto}
            onToggle={setDetranVeiculoFoto}
          />
        </div>

        {nfeAtivado && !creditosPrePagos && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Nota Fiscal está ativo, mas só emite quando Créditos Pré-Pagos também estiver ligado.
          </p>
        )}

        {/* ART Múltipla — número/vencimento (só quando o switch está ligado, mesmo
            antes de salvar). Sem isso o Owner não tinha onde cadastrar a ART da
            vistoriadora e ficava preso na validação "insira a ART antes de ativar". */}
        {artMultipla && (
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-2">
            <label className="text-xs font-semibold text-violet-700 uppercase tracking-wide flex items-center gap-1.5">
              <FileSignature className="w-3.5 h-3.5" /> ART da Vistoriadora
              {v.art && <span className="text-emerald-600 ml-1">✓ configurada</span>}
            </label>
            <div className="flex gap-2 max-w-sm">
              <Input
                placeholder="Número da ART"
                value={art}
                onChange={(e) => setArt(e.target.value)}
                className="font-mono text-xs h-8 bg-white"
              />
              <Input
                type="date"
                value={vencimentoArt}
                onChange={(e) => setVencimentoArt(e.target.value)}
                className="text-xs h-8 bg-white"
              />
            </div>
          </div>
        )}

        {/* Preço por Laudo cobrado da Vistoriadora */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" />
            Preço por Laudo (cobrado da Vistoriadora)
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">R$</span>
            <Input
              type="number"
              min={0}
              step={0.01}
              placeholder="0,00"
              value={valorPorLaudo}
              onChange={(e) => setValorPorLaudo(e.target.value)}
              className="w-32 text-right font-semibold bg-white h-8 text-sm"
            />
            <span className="text-xs text-slate-400">/ laudo</span>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}

export default function AdminModulos() {
  const { profile, session, loading } = useAuth();
  const [, navigate] = useLocation();
  const isOwner = profile?.roles?.includes("owner") ?? false;

  const { data: vistoriadoras, isLoading } = useQuery<VistoriadoraModulos[]>({
    queryKey: ["/api/owner/modulos"],
    queryFn: async () => {
      const res = await fetch("/api/owner/modulos", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar");
      return res.json();
    },
    enabled: !!session && isOwner,
  });

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (!isOwner) {
    navigate("/");
    return null;
  }

  return (
    <Shell>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-3xl font-bold font-display text-slate-900">Módulos</h1>
        </div>
        <p className="text-muted-foreground mt-1 ml-12">
          Ative ou desative funcionalidades por vistoriadora
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        {MODULOS.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.key}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium",
                m.bgColor,
                m.borderColor,
                m.color,
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {m.label}
            </div>
          );
        })}
      </div>

      <div className="max-w-4xl space-y-4">
        {isLoading ? (
          Array(3)
            .fill(0)
            .map((_, i) => <Skeleton key={i} className="h-52 rounded-xl" />)
        ) : !vistoriadoras || vistoriadoras.length === 0 ? (
          <Card className="border-none shadow-md">
            <CardContent className="py-12 text-center text-slate-500">
              <Building2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhuma vistoriadora cadastrada</p>
            </CardContent>
          </Card>
        ) : (
          vistoriadoras.map((v) => (
            <VistoriadoraCard key={v.id} v={v} token={session?.access_token ?? ""} />
          ))
        )}
      </div>
    </Shell>
  );
}
